import { UserClaims } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import { config } from "../../config";
import {
  RoleName,
  SubscriptionType,
  UserRoleName,
  UserRoles,
  Workspace,
} from "../../interfaces/rbac";

type ReemiaUserClaim = UserClaims & {
  first_name: string;
  last_name: string;
  company_id: string;
  company_alphabetic_name: string;
  permissions: string;
};

export interface Session {
  user: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  org: {
    id: string;
    name: string;
  };
  roles?: UserRoles;
  OrionUserGroups: string[];
  OrionContributorGroups: string[];
  OrionManagerGroups: string[];
  OrionChatGroups: string[];
  SiriusUserGroups: string[];
  SiriusContributorGroups: string[];
  SiriusManagerGroups: string[];
  ManagerGroups: string[];
  isAdmin: boolean;
  workspaces: Workspace[];
}

const fetchWorkspaces = async (accessToken: string): Promise<Workspace[]> => {
  const response = await fetch(
    `${config.REACT_APP_RBAC_BACKEND_URL}/workspace`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status}`);
  }

  return response.json();
};

const useSession = () => {
  const { authState } = useOktaAuth();

  return useQuery({
    queryKey: ["session", authState?.accessToken?.accessToken],
    queryFn: async (): Promise<Session> => {
      if (
        !authState?.accessToken?.accessToken ||
        !authState.accessToken?.claims
      ) {
        throw new Error("No access token or claims available");
      }

      const claims = authState.accessToken.claims as ReemiaUserClaim;
      const roles = authState.accessToken.claims.permissions
        ? JSON.parse(authState.accessToken.claims.permissions as string)
        : undefined;
      const workspaces = await fetchWorkspaces(
        authState.accessToken.accessToken
      );

      return {
        user: {
          id: claims.sub,
          email: claims.sub,
          first_name: claims.first_name,
          last_name: claims.last_name,
        },
        org: {
          id: claims.company_id,
          name: claims.company_alphabetic_name,
        },
        roles: roles,
        OrionUserGroups: OrionUserGroups(roles),
        OrionContributorGroups: OrionContributorGroups(roles),
        OrionManagerGroups: OrionManagerGroups(roles),
        OrionChatGroups: OrionChatGroups(roles),
        SiriusUserGroups: SiriusUserGroups(roles),
        SiriusContributorGroups: SiriusContributorGroups(roles),
        SiriusManagerGroups: SiriusManagerGroups(roles),
        ManagerGroups: ManagerGroups(roles),
        isAdmin: isAdmin(roles),
        workspaces: workspaces,
      };
    },
    enabled:
      !!authState?.isAuthenticated &&
      !!authState?.accessToken?.accessToken &&
      !!authState?.accessToken?.claims,
    staleTime: 5 * 60 * 1000,
  });
};

export function OrionUserGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(([_, role]) =>
          [SubscriptionType.ORION, SubscriptionType.PLATFORM].includes(
            role.subscription
          )
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function OrionContributorGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(
          ([_, role]) =>
            [SubscriptionType.ORION, SubscriptionType.PLATFORM].includes(
              role.subscription
            ) && [RoleName.CONTRIBUTOR, RoleName.MANAGER].includes(role.role)
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function OrionManagerGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(
          ([_, role]) =>
            [SubscriptionType.ORION, SubscriptionType.PLATFORM].includes(
              role.subscription
            ) && RoleName.MANAGER === role.role
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function OrionChatGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(([_, role]) => SubscriptionType.PLATFORM === role.subscription)
        .map(([workspace, _]) => workspace)
    : [];
}

export function SiriusUserGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(([_, role]) =>
          [SubscriptionType.SIRIUS, SubscriptionType.PLATFORM].includes(
            role.subscription
          )
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function SiriusContributorGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(
          ([_, role]) =>
            [SubscriptionType.SIRIUS, SubscriptionType.PLATFORM].includes(
              role.subscription
            ) && [RoleName.CONTRIBUTOR, RoleName.MANAGER].includes(role.role)
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function SiriusManagerGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(
          ([_, role]) =>
            [SubscriptionType.SIRIUS, SubscriptionType.PLATFORM].includes(
              role.subscription
            ) && RoleName.MANAGER === role.role
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function ManagerGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(([_, role]) => role.role === RoleName.MANAGER)
        .map(([workspace, _]) => workspace)
    : [];
}

export function isAdmin(roles: UserRoles | undefined) {
  return !!roles && roles.role === UserRoleName.ADMIN;
}

export default useSession;
